import { useState } from "react"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Uploader } from "../../components/Uploader"
import { Button } from "../../components/Button"
import { ContentWrapper } from "../../components/Wrappers"
import { convert } from "../../services/convert"
import {
  WEB_PORTALS,
  STORAGE_ENCRYPTED_DATA,
  STORAGE_IV,
  STORAGE_ARTICLES_BY_MARTIN,
} from "../../constants"
import { getStorageVariable, setStorageVariable } from "../../utils/storage"
import { ProgressBar } from "../../components/ProgressBar"
import { CheckBox } from "../../components/Checkbox"
import { Tooltip } from "../../components/Tooltip"

const { slovakCryptoNews, slovakFtNews } = WEB_PORTALS

export const UploadDocs: React.FC = () => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [areArticlesByMartin, setAreArticlesByMartin] = useState(
    getStorageVariable(STORAGE_ARTICLES_BY_MARTIN) === "true"
  )

  const knData = getStorageVariable(
    `${slovakCryptoNews}${STORAGE_ENCRYPTED_DATA}`
  )
  const knIv = getStorageVariable(`${slovakCryptoNews}${STORAGE_IV}`)
  const ftnData = getStorageVariable(`${slovakFtNews}${STORAGE_ENCRYPTED_DATA}`)
  const ftnIv = getStorageVariable(`${slovakFtNews}${STORAGE_IV}`)

  const startImportHandler = async () => {
    setMessages([])
    setIsLoading(true)
    await convert(
      { knData, knIv, ftnData, ftnIv },
      (partialMessage) => {
        setMessages((prev) => [...prev, partialMessage])
      },
      areArticlesByMartin
    )
    setIsLoading(false)
  }

  const processingData = messages.map((msg, index) => {
    const parsedMsg = JSON.parse(msg)
    return parsedMsg
  })

  const transformData = (arr: any[]) => {
    const result = []
    let group = []
    let currentHeader = null

    for (let item of arr) {
      // Check if the item has a header in its data property.
      if (item.data && item.data.header) {
        // If we're starting a new header group, flush the old one.
        if (currentHeader === null || currentHeader !== item.data.header) {
          if (group.length > 0) {
            result.push(group)
            group = []
          }
          currentHeader = item.data.header
        }
        // Add the current item to the group.
        group.push(item)
      } else {
        // When an item does not have a header (e.g. "started" or "finished")
        // flush any existing header group first.
        if (group.length > 0) {
          result.push(group)
          group = []
          currentHeader = null
        }
        // Add the non-header item directly to the result.
        result.push(item)
      }
    }

    // If there's a remaining group, add it.
    if (group.length > 0) {
      result.push(group)
    }

    return result
  }

  const checkedHandler = () => {
    const currentValue =
      getStorageVariable(STORAGE_ARTICLES_BY_MARTIN) === "true"
    setStorageVariable(STORAGE_ARTICLES_BY_MARTIN, (!currentValue).toString())
    setAreArticlesByMartin(!currentValue)
  }

  const transformedData = transformData(processingData)

  const articleProcesses = transformedData.filter((item) => Array.isArray(item))

  const mappedArticleProcesses = articleProcesses.map((item) => {
    const lastItem = item.at(-1)
    const {
      message,
      data: { header, progress, url },
    } = lastItem
    return (
      <ProgressBar
        progress={progress}
        message={message}
        process="uploading"
        url={url}
        articleName={header}
      />
    )
  })

  return (
    <ContentWrapper>
      <Flex vertical gap="large">
        <Uploader />
        <Flex justify="center" vertical gap="large">
          {mappedArticleProcesses}
          <Tooltip text={t("uploading.articlesByMartinTooltip")}>
            <span style={{ width: "150px" }}>
              <CheckBox
                label={t("uploading.articlesByMartin")}
                checked={areArticlesByMartin}
                onChange={checkedHandler}
                disabled={isLoading}
              />
            </span>
          </Tooltip>
          <Button
            label={isLoading ? t("uploading.importing") : t("uploading.import")}
            type="primary"
            onClick={startImportHandler}
            style={{ width: "300px", margin: "0 auto" }}
            loading={isLoading}
          />
        </Flex>
      </Flex>
    </ContentWrapper>
  )
}
